/**
 * Small shared primitives for the staff training surfaces (TRUS-486).
 *
 * Deliberately thin: the sanctioned page chrome (PageHeader / PageContainer /
 * SectionHeader / StatTile / PageState) comes from `@/components/weavein`, and these fill
 * only the gaps that library does not cover — a status pill, a modal, form field wrappers
 * and a table shell. All styling is token-based per docs/WEAVEIN_UI_STANDARDS.md, so it
 * follows the theme without any `dark:` variants.
 */

import React, { useEffect, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, X } from 'lucide-react'

export const CARD = 'rounded-[10px] border border-v2border bg-surface shadow-v2-1'

type Tone = 'neutral' | 'accent' | 'success' | 'attention' | 'danger'

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'bg-paper-2 text-ink-500 border-v2border',
  accent: 'bg-v2accent-soft text-v2accent-ink border-v2accent',
  success: 'bg-success-soft text-success-ink border-success',
  attention: 'bg-attention-soft text-attention-ink border-attention',
  danger: 'bg-danger-soft text-danger border-danger',
}

export function Pill({ tone = 'neutral', children }: { tone?: Tone; children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium ${TONE_CLASS[tone]}`}
    >
      {children}
    </span>
  )
}

export function Modal({
  open,
  title,
  description,
  onClose,
  footer,
  children,
  wide = false,
}: {
  open: boolean
  title: string
  description?: string
  onClose: () => void
  footer?: React.ReactNode
  children: React.ReactNode
  wide?: boolean
}) {
  // Escape closes, and the body does not scroll behind the dialog.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/40 p-4 sm:p-8">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`${CARD} w-full ${wide ? 'max-w-3xl' : 'max-w-xl'} my-4`}
      >
        <div className="flex items-start justify-between gap-4 border-b border-v2border px-5 py-4">
          <div>
            <div className="font-display text-sm font-semibold tracking-[-0.01em] text-ink">{title}</div>
            {description && <div className="mt-1 text-[13px] text-ink-500">{description}</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-ink-400 hover:bg-paper-2 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-v2accent"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-v2border px-5 py-3">{footer}</div>}
      </div>
    </div>
  )
}

export const INPUT =
  'w-full rounded-md border border-v2border bg-surface px-3 py-2 text-[13px] text-ink placeholder:text-ink-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-v2accent'

export function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string
  hint?: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[13px] font-medium text-ink">
        {label}
        {required && <span className="ml-1 text-danger">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-[12px] text-ink-400">{hint}</span>}
    </label>
  )
}

export function Button({
  variant = 'secondary',
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger' }) {
  const base =
    'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-v2accent'
  const variants = {
    primary: 'bg-v2accent text-white hover:opacity-90',
    secondary: 'border border-v2border bg-surface text-ink hover:bg-paper-2',
    ghost: 'text-ink-500 hover:bg-paper-2 hover:text-ink',
    danger: 'border border-danger bg-danger-soft text-danger hover:opacity-90',
  }
  return <button className={`${base} ${variants[variant]} ${className}`} {...props} />
}

/** Table shell: the wrapper scrolls, so a wide column set never scrolls the page body. */
export function TableShell({ children }: { children: React.ReactNode }) {
  return (
    <div className={`${CARD} overflow-hidden`}>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">{children}</table>
      </div>
    </div>
  )
}

export function Th({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={`whitespace-nowrap border-b border-v2border bg-paper-2 px-3 py-2 text-left font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-ink-500 ${className}`}
    >
      {children}
    </th>
  )
}

// --- Sorting (TRUS-562: "they want to sort/filter in Training by staff") ------------

export type SortDir = 'asc' | 'desc'

/** Client-side sorting for a table. `accessors` maps a column key to the value to sort
 *  by; strings compare case-insensitively, empty values always sort last. */
export function useSort<T>(
  rows: T[],
  accessors: Record<string, (row: T) => string | number | null | undefined>,
  initial: { key: string; dir: SortDir },
) {
  const [sort, setSort] = useState(initial)
  const sorted = useMemo(() => {
    const get = accessors[sort.key]
    if (!get) return rows
    const factor = sort.dir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const va = get(a)
      const vb = get(b)
      const ea = va === null || va === undefined || va === ''
      const eb = vb === null || vb === undefined || vb === ''
      if (ea || eb) return ea === eb ? 0 : ea ? 1 : -1
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * factor
      return String(va).localeCompare(String(vb), undefined, { sensitivity: 'base', numeric: true }) * factor
    })
    // accessors are recreated each render; the sort only needs to follow rows and sort
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort])
  const toggle = (key: string) =>
    setSort((cur) => (cur.key === key ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))
  return { sorted, sort, toggle }
}

/** A column header that sorts its table when clicked. */
export function SortTh({
  label,
  sortKey,
  sort,
  onSort,
  className = '',
}: {
  label: string
  sortKey: string
  sort: { key: string; dir: SortDir }
  onSort: (key: string) => void
  className?: string
}) {
  const active = sort.key === sortKey
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown
  return (
    <Th className={className}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        aria-label={`Sort by ${label}`}
        className={`inline-flex items-center gap-1 uppercase tracking-[0.14em] hover:text-ink ${active ? 'text-ink' : ''}`}
      >
        {label}
        <Icon className="h-3 w-3" aria-hidden="true" />
      </button>
    </Th>
  )
}

/** A row of filter controls above a table; wraps on narrow screens. */
export function FilterBar({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap items-end gap-2">{children}</div>
}

export const FILTER_INPUT =
  'rounded-md border border-v2border bg-surface px-2.5 py-1.5 text-[13px] text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-v2accent'

export function Td({
  children,
  className = '',
  colSpan,
}: {
  children?: React.ReactNode
  className?: string
  colSpan?: number
}) {
  return (
    <td colSpan={colSpan} className={`border-b border-v2border-muted px-3 py-2 align-top text-ink ${className}`}>
      {children}
    </td>
  )
}

/** Inline error banner for a failed action, so a page never fails silently. */
export function ErrorNote({ message, onDismiss }: { message: string; onDismiss?: () => void }) {
  return (
    <div
      role="alert"
      className="flex items-start justify-between gap-3 rounded-[10px] border border-danger bg-danger-soft px-4 py-3 text-[13px] text-danger"
    >
      <span>{message}</span>
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="shrink-0">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}
